import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save, Copy, Plus } from "lucide-react";
import { toast } from "sonner";

const COLS = [
  { key: "veteran_alone", label: "Veteran alone" },
  { key: "with_spouse", label: "With spouse" },
  { key: "with_spouse_one_parent", label: "+ 1 parent" },
  { key: "with_spouse_two_parents", label: "+ 2 parents" },
  { key: "one_parent", label: "One parent only" },
  { key: "two_parents", label: "Two parents only" },
  { key: "child_under_18_add", label: "Child <18 (add-on)" },
  { key: "child_school_add", label: "Child in school (add-on)" },
  { key: "spouse_aa_add", label: "Spouse A&A (add-on)" },
];

export default function RatesManager() {
  const { user } = useAuth();
  const [year, setYear] = useState(2026);
  const [years, setYears] = useState([]);
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState({});
  const [newYear, setNewYear] = useState("");

  const load = async (y = year) => {
    const [yr, rt] = await Promise.all([api.get("/rates/years"), api.get(`/rates?year=${y}`)]);
    setYears(yr.data);
    setRows(rt.data.rows);
  };
  useEffect(() => { load(year); /* eslint-disable-next-line */ }, [year]);

  const updateCell = (rating, col, val) => {
    const v = parseFloat(val);
    setRows(rows.map((r) => r.rating === rating ? { ...r, [col]: isNaN(v) ? 0 : v } : r));
  };

  const saveRow = async (row) => {
    setSaving((s) => ({ ...s, [row.rating]: true }));
    try {
      await api.put("/rates", { ...row, year });
      toast.success(`Saved ${row.rating}%`);
    } catch { toast.error("Save failed"); }
    finally { setSaving((s) => ({ ...s, [row.rating]: false })); }
  };

  const saveAll = async () => {
    for (const row of rows) await api.put("/rates", { ...row, year });
    toast.success("All rows saved");
  };

  const duplicateYear = async () => {
    const to = parseInt(newYear);
    if (!to) return toast.error("Enter a year");
    try {
      await api.post(`/rates/duplicate?from_year=${year}&to_year=${to}`);
      toast.success(`Cloned ${year} → ${to}`);
      setNewYear("");
      load(year);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!user || user.role !== "admin") return <div className="p-24 text-center">Admin only.</div>;
  if (!rows) return <div className="grid place-items-center h-96"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="mx-auto max-w-[1600px] px-5 lg:px-10 py-10">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link to="/admin" className="text-sm text-primary/60 hover:text-primary font-semibold" data-testid="rates-back-admin">← Back to Admin</Link>
          <div className="label-caps text-primary/70 mt-4">Admin · Rates</div>
          <h1 className="font-display text-3xl lg:text-5xl mt-1">Compensation rate tables</h1>
          <p className="text-primary/70 mt-2">Editable per year. Rows drive both the public calculator and Pamela's coaching math.</p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <Label>Year</Label>
            <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="mt-2 h-11 px-3 border-2 border-primary bg-white font-semibold" data-testid="year-select">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <Label>Add new year (clone from {year})</Label>
            <div className="mt-2 flex gap-2">
              <Input type="number" placeholder="e.g. 2027" value={newYear} onChange={(e) => setNewYear(e.target.value)} className="w-32 h-11" data-testid="new-year-input" />
              <Button size="sm" variant="outline" onClick={duplicateYear} data-testid="clone-year"><Copy className="w-4 h-4" /> Clone</Button>
            </div>
          </div>
          <Button onClick={saveAll} data-testid="save-all"><Save className="w-4 h-4" /> Save all</Button>
        </div>
      </div>

      <Card className="p-0 overflow-x-auto border-2">
        <table className="min-w-[1400px] w-full text-sm" data-testid="rates-table">
          <thead className="bg-secondary">
            <tr>
              <th className="text-left p-3 border-r border-border font-semibold sticky left-0 bg-secondary z-10">Rating</th>
              {COLS.map((c) => <th key={c.key} className="text-left p-3 border-r border-border font-semibold whitespace-nowrap">{c.label}</th>)}
              <th className="p-3">Save</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.rating} className="border-t border-border hover:bg-secondary/40" data-testid={`row-${r.rating}`}>
                <td className="p-3 font-display text-lg border-r border-border sticky left-0 bg-white">{r.rating}%</td>
                {COLS.map((c) => (
                  <td key={c.key} className="p-2 border-r border-border">
                    <input type="number" step="0.01" value={r[c.key] || 0}
                      onChange={(e) => updateCell(r.rating, c.key, e.target.value)}
                      className="w-28 px-2 py-1.5 border border-border rounded bg-white focus:border-primary focus:outline-none font-mono text-right"
                      data-testid={`cell-${r.rating}-${c.key}`} />
                  </td>
                ))}
                <td className="p-2 text-center">
                  <Button size="sm" onClick={() => saveRow(r)} disabled={saving[r.rating]} data-testid={`save-${r.rating}`}>
                    {saving[r.rating] ? "…" : <Save className="w-3.5 h-3.5" />}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <p className="mt-6 text-xs text-primary/60 max-w-3xl leading-relaxed">
        Verify every dependent figure against <a className="link-underlined" href="https://www.va.gov/disability/compensation-rates/veteran-rates/" target="_blank" rel="noreferrer">va.gov/disability/compensation-rates</a> before launch.
        The 2026 rows are seeded with a 2.8% COLA bump on prior VA additions and marked ADMIN-EDITABLE for verification.
      </p>
    </div>
  );
}
